<template>
    <div>
        <!-- Wallet Mobile Card -->
        <article
            @click="handleCardClick"
            class="w-full p-4 pb-2 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 flex flex-col gap-4"
            :class="{
                'shadow-lg dark:shadow-slate-900/50 rounded-lg': hasShadow,
                'border-b': hasShadow || index !== totalCards - 1,
            }"
        >
            <!-- Card Header with Bank Info -->
            <header class="w-full flex items-center gap-4 justify-between">
                <span class="flex items-center gap-2">
                    <ImageLoader class="shrink-0">
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${card.card?.card_image?.path}/_small/${card.card?.card_image?.file_name}`"
                                class="max-w-[80px] lg:max-w-[80px] h-auto relative z-20"
                                alt="Card image"
                            />
                        </template>
                        <template #error>
                            <div
                                class="rounded flex items-center justify-center bg-slate-200 dark:bg-slate-700 w-20 h-12"
                            >
                                <Icon icon="credit-card" class="h-6 w-6" />
                            </div>
                        </template>
                    </ImageLoader>

                    <!-- Card Name -->
                    <h2 class="text-slate-800 dark:text-slate-100 text-base font-medium">
                        <span v-if="card.card_id">{{ card.card_title }}</span>
                        <span v-else-if="card.card_match_request">
                            {{ card.card_match_request.card_not_found_name }}
                        </span>
                        <span v-else>Unknown card</span>

                        <Transition name="fade">
                            <span
                                class="text-xs ml-1 text-slate-500 dark:text-slate-400"
                                v-if="
                                    card.last_four &&
                                    !card.card_match_request &&
                                    showLastFour
                                "
                            >
                                ({{ card.last_four }})
                            </span>
                        </Transition>
                    </h2>
                </span>
                <div class="flex items-center gap-2 relative">
                    <button
                        v-if="!card.card_id"
                        class="btn-base btn-white"
                        @click.stop="openCardMatchModal(card)"
                    >
                        Match
                    </button>
                    <div
                        v-if="
                            card.card_match_request &&
                            card.card_match_request.rejected
                        "
                        class="absolute -bottom-1.5 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-red-500 text-white shadow-lg"
                    >
                        <Icon icon="alert" class="w-2.5 h-2.5" />
                    </div>
                </div>
            </header>

            <!-- Card Details -->
            <dl class="w-full">
                <!-- Status Row -->
                <div
                    class="py-2 grid grid-cols-2 gap-1 items-center"
                    v-if="!card.unmatched"
                >
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                    >
                        <Icon icon="card-check" class="w-5 h-5" />
                        <span>Status</span>
                    </dt>
                    <dd
                        class="text-sm text-left font-medium leading-5 text-slate-800 dark:text-slate-100"
                    >
                        <span
                            v-if="card.card_status === 'open'"
                            class="inline-flex items-center px-3 py-0.5 font-medium rounded-full bg-emerald-50 text-emerald-500 capitalize"
                        >
                            Open
                        </span>
                        <span
                            v-else-if="card.card_status === 'closed'"
                            class="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 font-medium text-red-700"
                        >
                            Closed
                        </span>
                        <span
                            v-else
                            class="inline-flex items-center px-3 py-0.5 font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 capitalize"
                        >
                            {{ card.card_status || 'Unknown' }}
                        </span>
                    </dd>
                </div>

                <!-- User Row -->
                <div class="py-2 grid grid-cols-2 gap-1 items-center">
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                    >
                        <Icon icon="user" class="w-5 h-5" />
                        <span>User</span>
                    </dt>
                    <dd
                        class="text-sm text-left font-medium leading-5 text-slate-800 dark:text-slate-100"
                    >
                        {{ formatEntityName(card.user_name) }}
                    </dd>
                </div>

                <!-- Date Opened Row -->
                <div class="py-2 grid grid-cols-2 gap-1 items-center">
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                    >
                        <Icon icon="calendar" class="w-5 h-5" />
                        <span>Date opened</span>
                    </dt>
                    <dd
                        class="text-sm text-left font-medium leading-5 text-slate-800 dark:text-slate-100"
                    >
                        {{ formatDate(card.date_opened) }}
                    </dd>
                </div>

                <!-- Chase 5/24 Status Row -->
                <div
                    v-if="card.contributes_to_wallet_five_twenty_four"
                    class="py-2 grid grid-cols-2 gap-1 items-center"
                >
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                    >
                        <Icon icon="credit-cards" class="w-5 h-5" />
                        <span>5/24</span>
                    </dt>
                    <dd
                        class="text-sm text-left font-medium leading-5 text-slate-800 dark:text-slate-100"
                    >
                        <span
                            class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                        >
                            {{ card.five_twenty_four_level }}/24
                        </span>
                    </dd>
                </div>
            </dl>
        </article>
        <div
            class="gap-1 rounded-b-lg bg-slate-50 dark:bg-slate-700/50 w-full py-2.5 px-2 border-b border-x border-slate-200 dark:border-slate-700 h-8 flex items-center justify-center text-xs shadow-lg"
            :class="{ 'text-slate-500': !connectionProblem, 'text-red-500': connectionProblem }"
            v-show="card?.member_guid && !card?.converted_to_manual"
        >
            <Icon icon="bank-financial" class="h-4 w-4" />
            <span>Connected via bank{{ connectionProblem ? ': Error' : '' }}</span>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { formatEntityName } from '@/utils/entityNameFormatter.js';

const walletStore = useWalletStore();
const { showLastFour } = storeToRefs(walletStore);
const { setSelectedCard, setAccountSidebar, openCardMatchModal, getConnectionProblemForCard } = walletStore;

const props = defineProps({
    index: {
        type: Number,
        default: 0,
    },
    totalCards: {
        type: Number,
        default: 0,
    },
    card: {
        type: Object,
        required: true,
    },
    hasShadow: {
        type: Boolean,
        default: true,
    },
});

const cdnUrl = ref(window.cdnUrl);

const formatDate = (dateString) => {
    if (!dateString) return '--';
    return dayjs(dateString).format('MMM D, YYYY');
};

const connectionProblem = computed(() => {
    return getConnectionProblemForCard(props.card);
});

const handleCardClick = () => {
    if (!props.card.card_id) return;
    setSelectedCard(props.card);
    setAccountSidebar(true);
};
</script>
